import {EventEmitter, Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {ReportTableStoreState} from './report-table';
import {AgGridEvent, ColDef, ICellRendererParams, IServerSideGetRowsParams} from '@ag-grid-community/core';
import {Observable, Subscription, switchMap, throwError, withLatestFrom} from 'rxjs';
import {catchError, filter, finalize, first, tap} from 'rxjs/operators';
import {ReportStore} from './report.store';
import {AdReportParams, AdRequestOptions, AdRequestParams} from '../../interfaces/ad-reports';
import {ReportStoreState} from './report';
import {ReportsTableService} from '../../services/reports-table.service';
import {IServerSideDatasource} from '@ag-grid-enterprise/all-modules';
import {ReportsRequestsService} from '../../services/reports-requests.service';
import {Site} from '../../interfaces/site';
import {Serializer} from '../../interfaces/serializer';
import {AppState} from '../store';
import {Store} from '@ngrx/store';
import {selectSite} from '../init/init.selectors';
import {dialogOpen} from '../dialog/dialog.actions';
import {TrendsDialogComponent} from '../../components/trends-dialog/trends-dialog.component';
import {selectDimensionComponentObjects, selectMetricComponentObjects} from '../selectors';
import {DimensionComponentObject, DimensionComponentObjects} from '../../classes/dimension-component-object';
import {MetricComponentObject, MetricComponentObjects} from '../../classes/metric-component-object';

@Injectable()
export class ReportTableStore extends ComponentStore<ReportTableStoreState> {
  private _requestSubscription: Subscription | undefined;

  private readonly _dataSet$: Observable<Serializer<Site>> = this._store.select(selectSite);

  public readonly rowCount$: Observable<number> = this.select((state: ReportTableStoreState): number => state.rowCount);
  public readonly pageSize$: Observable<number> = this.select((state: ReportTableStoreState): number => state.pageSize);

  public readonly onChanges$: EventEmitter<AgGridEvent> = new EventEmitter<AgGridEvent>();

  private readonly _params: (first: boolean) => void = this.effect((params$: Observable<boolean>): Observable<Array<ColDef>> => params$.pipe(
    switchMap((first: boolean): Observable<Array<ColDef>> => this._reportStore.reportParamsSubmitted$.pipe(
      withLatestFrom(this._reportStore.state$, this.state$, this._dataSet$),
      filter(([params, reportStoreState]: [Partial<AdReportParams> | null, ReportStoreState, ReportTableStoreState, Serializer<Site>]): boolean => reportStoreState.submitted),
      switchMap(([reportParams, reportStoreState, reportTableStoreState, dataSet]: [Partial<AdReportParams>, ReportStoreState, ReportTableStoreState, Serializer<Site>]) => this._tableService.initColumnDefs(reportStoreState.reportParams, reportStoreState.requestParams, reportStoreState.reportType).pipe(
        tap((columnDefs: Array<ColDef>): void => {
          reportTableStoreState.agGrid.api.setColumnDefs(columnDefs);

          if (!first) {
            this._tableService.initColumnStates(reportTableStoreState.agGrid, reportStoreState.requestParams);
            reportTableStoreState.agGrid.api.setServerSideDatasource(
              this._createDatasource(
                dataSet,
                reportStoreState.uuid,
                reportStoreState.token,
                reportTableStoreState.agGrid,
                reportStoreState.requestParamsSubmitted,
                reportStoreState.requestOptions,
                reportStoreState.limit,
                reportTableStoreState.suppressTotals,
                reportTableStoreState.suppressPageTotals,
                reportStoreState.report?.id || null,
                reportStoreState.template?.id || null
              )
            );
          }

          this.patchState({
            pageSize: reportStoreState.limit || this._limit
          })
        })
      ))
    ))
  ));

  private readonly _reloadActions$: Subscription = this.effect((): Observable<[Partial<AdRequestOptions>, ReportStoreState]> => this._reportStore.requestOptionsChanges$.pipe(
    withLatestFrom(this._reportStore.state$),
    filter(([options, reportStoreState]: [Partial<AdRequestOptions>, ReportStoreState]): boolean => reportStoreState.submitted),
    tap(([]: [Partial<AdRequestOptions>, ReportStoreState]): void => {
      this._params(false);
    })
  ));

  private readonly _updateColumnsOrder: (params: { dimensions: Array<string>, metrics: Array<string> }) => void = this.effect((params$: Observable<any>): Observable<any> => params$.pipe(
    withLatestFrom(
      this._store.select(selectDimensionComponentObjects),
      this._store.select(selectMetricComponentObjects)
    ),
    tap(([params, dimensions, metrics]: [{ dimensions: Array<string>, metrics: Array<string> }, DimensionComponentObjects, MetricComponentObjects]): void => {
      this._reportStore.updateReportParams({
        dimensions: params.dimensions.map((slug: string): DimensionComponentObject => dimensions.find((dimension: DimensionComponentObject): boolean => dimension.payload.attributes.slug === slug)),
        metrics: params.metrics.map((slug: string): MetricComponentObject => metrics.find((metric: MetricComponentObject): boolean => metric.payload.attributes.slug === slug))
      });
    })
  ));

  // #############################
  // DIALOGS
  // #############################

  public readonly trendsDialog: (params: { agGridParams: ICellRendererParams }) => void = this.effect((params$: Observable<{ agGridParams: ICellRendererParams }>): Observable<[{ agGridParams: ICellRendererParams }, ReportStoreState, Serializer<Site>]> => params$.pipe(
    withLatestFrom(this._reportStore.state$, this._dataSet$),
    tap(([params, reportStoreState, dataset]: [{ agGridParams: ICellRendererParams }, ReportStoreState, Serializer<Site>]): void => {
      this._store.dispatch(dialogOpen({
        component: TrendsDialogComponent,
        disableClose: false,
        config: {
          width: '1000px',
          height: 'auto'
        },
        data: {
          dataset: dataset,
          params: reportStoreState.requestParams,
          uuid: reportStoreState.uuid,
          token: reportStoreState.token,
          options: reportStoreState.requestOptions,
          ...params.agGridParams
        }
      }));
    })
  ));

  private readonly _limit: number = 25;

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _reportStore: ReportStore,
    private readonly _tableService: ReportsTableService,
    private readonly _requestsService: ReportsRequestsService,
  ) {
    super({
      agGrid: null,
      data: null,
      rowCount: 0,
      pageSize: null,
      suppressTotals: false,
      suppressPageTotals: false,
      loading: false
    });
  }

  public init(
    agGrid: AgGridEvent,
    suppressTotals: boolean = false,
    suppressPageTotals: boolean = false,
  ): void {
    this.patchState({
      agGrid,
      suppressTotals,
      suppressPageTotals,
    });

    this._params(true);
    this._reportStore.updateLimit(this._limit);
  }

  private _createDatasource(
    dataSet: Serializer<Site>,
    uuid: string | null,
    token: string | null,
    agGrid: AgGridEvent,
    params: Partial<AdRequestParams>,
    options: Partial<AdRequestOptions>,
    pageSize: number,
    suppressTotals: boolean,
    suppressPageTotals: boolean,
    reportId: number | null = null,
    templateId: number | null = null,
    init: boolean = false,
  ): IServerSideDatasource {
    this._requestSubscription?.unsubscribe();

    return {
      getRows: (agGridParams: IServerSideGetRowsParams): void => {
        agGridParams.api.showLoadingOverlay();

        this._requestSubscription?.unsubscribe();
        this._requestSubscription = this._requestsService.getAdReport(
          dataSet,
          uuid,
          token,
          params,
          options,
          pageSize,
          agGridParams,
          reportId,
          templateId
        )
          .pipe(
            first(),
            catchError((err: never): Observable<never> => {
              agGridParams.fail();
              return throwError(err);
            }),
            finalize((): void => {
              agGridParams.api.hideOverlay();
            })
          )
          .subscribe((data): void => {
            this.patchState({
              rowCount: data.report.size.all
            });

            if (!suppressTotals) {
              if (suppressPageTotals) {
                data.pinnedBottomRows.shift();
              }
              agGridParams.api.setPinnedBottomRowData(data.pinnedBottomRows);
            }

            agGridParams.success({
              rowData:  data.rows,
              rowCount: data.report.size.all
            });

            this.onChanges$.emit(agGrid);

            if (!init) {
              const filters: { [key: string]: any } = this._tableService.getFilters(params);

              if (Object.keys(filters).length) {
                agGridParams.api.setFilterModel(filters);
              }
            }

            this._requestSubscription?.unsubscribe();
            init = true;
          });
      }
    };
  }

  public changePaginationPageSize(limit: number): void {
    this.patchState({
      pageSize: limit
    });

    this._reportStore.updateLimit(limit);
    this._params(false);
  }

  public updateColumnsOrder(dimensions: Array<string>, metrics: Array<string>): void {
    this._updateColumnsOrder({ dimensions: dimensions, metrics: metrics });
  }

}
